<template>
  <div :class="{ 'menuContainer': showMenu }">
    <div
      class="asHeaderMobile"
      :class="{
        '-withoutPoints': !showPoints
      }"
      data-cy="asHeaderChat"
      @keyup.esc="onHandlerEsc"
      tabindex="0"
    >
      <div v-if="showBack" class="asHeaderMobile_col -back" @click="goBack">
        <a class="asHeaderMobile_back">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#clip0_3978_3062)">
              <path d="M10.828 12.0007L15.071 16.2437C15.4615 16.6342 15.4615 17.2673 15.071 17.6577C14.6805 18.0482 14.0475 18.0482 13.657 17.6577L8.70711 12.7078C8.31658 12.3173 8.31658 11.6841 8.70711 11.2936L13.657 6.34372C14.0475 5.95325 14.6805 5.95325 15.071 6.34372C15.4615 6.73418 15.4615 7.36725 15.071 7.75772L10.828 12.0007Z" fill="#B4B8BB"/>
            </g>
            <defs>
              <clipPath id="clip0_3978_3062">
                <rect width="24" height="24" fill="white"/>
              </clipPath>
            </defs>
          </svg>
        </a>
      </div>

      <div class="asHeaderMobile_col -name">
        <img v-if="avatar" class="asHeaderMobile_avatar" :src="avatar" :alt="title">
        <div class="asHeaderMobile_col -title">
          <div class="asHeaderMobile_title">
            {{ title }}
          </div>
          <router-link v-if="showPoints" class="asHeaderMobile_points" :to="{ name: 'points' }">
            <ac-points-label class="-enabled" :points="userScoreAmount"/>
          </router-link>
        </div>
      </div>

      <slot name="menu">
        <div class="asHeaderMobile_col -nav">
          <template v-if="nav">
            <span @click="toggleMenu" class="asHeaderMobile_menu">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_3976_44)">
                  <path d="M3 5C3 4.44772 3.44772 4 4 4H20C20.5523 4 21 4.44772 21 5C21 5.55228 20.5523 6 20 6H4C3.44772 6 3 5.55228 3 5ZM3 12C3 11.4477 3.44772 11 4 11H14C14.5523 11 15 11.4477 15 12C15 12.5523 14.5523 13 14 13H4C3.44772 13 3 12.5523 3 12ZM3 19C3 18.4477 3.44772 18 4 18H20C20.5523 18 21 18.4477 21 19C21 19.5523 20.5523 20 20 20H4C3.44772 20 3 19.5523 3 19Z" fill="#0E2242"/>
                </g>
                <defs>
                  <clipPath id="clip0_3976_44">
                    <rect width="24" height="24" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            </span>
          </template>
        </div>
      </slot>
    </div>

    <as-menu
      v-if="showMenu"
      :menu="menu"
      classList="lSetting_asMenu -setting asHeaderMobile_menuContent"
      @click:item="selectMenuItem"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from 'vue'
import { RouteLocationRaw, useRouter } from 'vue-router'
import AsMenu from '@web/components/AsMenu.vue'
import { useStore } from '@web/store'
import AcPointsLabel from '@web/components.v2/PointsLabel.vue'
import Analytics from '@web/services/Analytics/Analytics'

export default defineComponent({
  props: {
    avatar: {
      type: String,
      default: ''
    },
    title: {
      title: String,
      default: ''
    },
    nav: {
      type: Boolean,
      default: true
    },
    customBack: {
      type: Boolean,
      default: false
    },
    back: {
      type: Boolean,
      default: false
    },
    prevPage: {
      type: [String, Object]
    },
    showPoints: {
      type: Boolean,
      default: false
    }
  },
  components: {
    AcPointsLabel,
    AsMenu
  },
  emits: ['goBack', 'menu:toggle', 'menu:open', 'menu:close', 'item:select'],
  setup (props, ctx) {
    const showMenu = ref<boolean>(false)
    function openMenu () {
      showMenu.value = true
      ctx.emit('menu:open')
      ctx.emit('menu:toggle', showMenu.value)
      Analytics.send({
        action: 'open_burger',
        project: 'platform'
      })
    }
    function closeMenu () {
      showMenu.value = false
      ctx.emit('menu:close')
      ctx.emit('menu:toggle', showMenu.value)
      Analytics.send({
        action: 'close_burger',
        project: 'platform'
      })
    }
    function toggleMenu () {
      if (showMenu.value) {
        closeMenu()
      } else {
        openMenu()
      }
    }

    const router = useRouter()
    const showBack = computed(() => props.back || props.prevPage || props.customBack)
    function goBack () {
      if (showMenu.value) {
        closeMenu()
      } else if (props.customBack) {
        ctx.emit('goBack')
      } else {
        if (props.prevPage) {
          router.push(props.prevPage as RouteLocationRaw)
        } else {
          router.go(-1)
        }
      }
    }

    const store = useStore()
    const menu = computed(() => store.getters['menu/mobile'])
    const userScoreAmount = computed(() => store.getters['userScore/amount'])

    function selectMenuItem (item) {
      ctx.emit('item:select', item)
      closeMenu()
    }

    function onHandlerEsc () {
      if (showMenu.value) {
        closeMenu()
      }
    }

    return {
      showBack,
      goBack,
      menu,
      toggleMenu,
      showMenu,
      selectMenuItem,
      userScoreAmount,
      onHandlerEsc
    }
  }
})
</script>

<style lang="scss">
@import '@web/assets/scss/vars.scss';

.asHeaderMobile {
  display: flex;
  height: 65px;
  //background: #F8F8FC;
  background: var(--ac-color-white);
  width: 100vw;
  overflow: hidden;
  //border-bottom: #E5E5E5 solid 5px;
  font-family: Inter, sans-serif;
  flex-shrink: 0;
  box-shadow: 0 5px 15px rgba(180, 184, 187, 0.35);
  position: relative;
  z-index: 1;

  &_col {
    &.-back {
      flex-shrink: 0;
    }

    &.-name {
      display: flex;
      align-items: center;
      max-width: calc(100vw - 60px);
      width: 100%;
    }

    &.-title {
      display: flex;
      flex-flow: column nowrap;
      align-items: flex-start;
      justify-content: flex-start;
    }

    &.-nav {
      flex-shrink: 0;
      margin-left: auto;
      /* ширина кнопки меню */
      width: 40px + 16px * 2;
    }
  }

  &.-withoutPoints .-name {
    justify-content: center;
  }

  &.-withoutPoints &_title {
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    color: var(--ac-color-black);
  }

  &_back {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 60px;
    width: auto;
    //padding: 0 16px;
    padding-left: 20px;
    padding-right: 6px;
    //box-sizing: content-box;
    //background-image: url(../../assets/images/icon-back-new.svg);
    //background-repeat: no-repeat;
    //background-position: center center;
  }

  &_avatar {
    display: block;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    border-radius: 50%;
    margin: 10px 16px;
    overflow: hidden;
    object-fit: cover;
    object-position: center;
  }

  &_title {
    font-family: Inter, sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 14px;
    color: var(--ac-color-black);

    &:only-child {
      width: 100%;
      text-align: center;
    }

    &:first-letter {
      text-transform: capitalize;
    }
  }

  &_points {
    display: block;
    max-width: 250px;
    margin-top: 6px;
    text-decoration: none;
  }

  &_menu {
    position: relative;
    display: flex;
    height: 40px;
    width: 40px;
    padding: 10px 16px;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    box-sizing: content-box;
    background: transparent;
    flex-shrink: 0;

    span {
      position: relative;
      z-index: 1;
      display: block;
      width: 20px;
      height: 2px;
      border-radius: 2px;
      background-color: #1E1F22;
      margin-bottom: 4px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  &_menuContent {
    padding-top: 12px;
  }
}
</style>

<style lang="scss" scoped>
.menuContainer {
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  min-height: 100%;
  overflow: auto;
  max-height: 100%;
}
</style>
